import { useEffect, useState } from "react";
import { useParams } from "@tanstack/react-router";
import { getUser, getUsersApi, useCore } from "@praesto/core";
import type { GovernedBy, Leave, LeaveType, VacationBalance } from "@praesto/shared-types";
import {
  Input,
  Select,
  Button,
  FormField,
  Card,
  Badge,
  StatCard,
  EmptyState,
  SkeletonList,
  SkeletonCardListItem,
  LeaveBadge,
} from "@praesto/ui-kit";

const statusLabel: Record<Leave["status"], string> = {
  pending: "In attesa",
  approved: "Approvata",
  rejected: "Rifiutata",
  cancelled: "Annullata",
};
const statusTone: Record<Leave["status"], "warning" | "success" | "danger" | "neutral"> = {
  pending: "warning",
  approved: "success",
  rejected: "danger",
  cancelled: "neutral",
};

export function ProfileLeavesTab() {
  const core = useCore();
  const { uid } = useParams({ strict: false }) as { uid: string };
  const [leaves, setLeaves] = useState<Leave[]>([]);
  const [balance, setBalance] = useState<VacationBalance | null>(null);
  const [governedBy, setGovernedBy] = useState<GovernedBy | undefined>(undefined);
  const [primaryHotelId, setPrimaryHotelId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<{
    type: LeaveType;
    startDate: string;
    endDate: string;
    reason: string;
  }>({
    type: "vacation",
    startDate: "",
    endDate: "",
    reason: "",
  });

  async function reload() {
    const api = getUsersApi();
    const tok = await api.getAuthToken();
    const [userRes, leavesRes] = await Promise.all([
      getUser(api.apiBase, uid, tok),
      fetch(`${api.apiBase}/api/users/${uid}/leaves`, {
        headers: { Authorization: `Bearer ${tok}` },
      }).then((r) => r.json() as Promise<{ leaves: Leave[] }>),
    ]);
    const u = userRes as unknown as {
      user: { vacationBalance?: VacationBalance; governedBy?: GovernedBy };
      memberships?: ReadonlyArray<{ hotelId: string }>;
    };
    setBalance(u.user.vacationBalance ?? null);
    setGovernedBy(u.user.governedBy);
    setPrimaryHotelId(u.memberships?.[0]?.hotelId ?? "");
    // sort newest start first
    setLeaves(leavesRes.leaves.slice().sort((a, b) => b.startDate.localeCompare(a.startDate)));
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    reload()
      .catch((e) => !cancelled && setError(String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  async function submit() {
    setError(null);
    if (!form.startDate || !form.endDate) {
      setError("Date richieste");
      return;
    }
    if (!primaryHotelId) {
      setError("Persona senza hotel di riferimento");
      return;
    }
    const api = getUsersApi();
    const tok = await api.getAuthToken();
    const body: Record<string, unknown> = {
      userId: uid,
      hotelId: primaryHotelId,
      type: form.type,
      startDate: form.startDate,
      endDate: form.endDate,
    };
    if (form.reason) body["reason"] = form.reason;
    const res = await fetch(`${api.apiBase}/api/users/leaves`, {
      method: "POST",
      headers: { Authorization: `Bearer ${tok}`, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = (await res.json()) as { error?: string };
      setError(err.error ?? "Errore");
      return;
    }
    setShowForm(false);
    setForm({ type: "vacation", startDate: "", endDate: "", reason: "" });
    await reload();
  }

  async function cancel(id: string) {
    const api = getUsersApi();
    const tok = await api.getAuthToken();
    await fetch(`${api.apiBase}/api/users/leaves/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${tok}` },
    });
    await reload();
  }

  if (loading) return <SkeletonList count={4} item={<SkeletonCardListItem />} />;

  return (
    <div className="space-y-6 max-w-3xl">
      {error && <p className="text-sm text-danger">{error}</p>}

      <section>
        <div className="text-eyebrow mb-3 flex items-center gap-2">
          <span>Saldo ferie {balance?.year ?? new Date().getFullYear()}</span>
          {governedBy?.capabilities.includes("balance") && (
            <Badge tone="info" variant="soft" size="xs">
              {core.i18n.t("people.governedBy", { provider: governedBy?.provider ?? "" })}
            </Badge>
          )}
        </div>
        {balance ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard
              label="Residui"
              value={String(Math.max(0, balance.totalDays - balance.usedDays))}
            />
            <StatCard label="Usati" value={String(balance.usedDays)} />
            <StatCard label="In attesa" value={String(balance.pendingDays)} />
            <StatCard label="Totali" value={String(balance.totalDays)} />
          </div>
        ) : (
          <EmptyState
            title="Saldo non impostato"
            description="Chiedi al titolare di impostare il saldo ferie di questa persona."
            size="compact"
          />
        )}
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <div className="text-eyebrow">Richieste</div>
          {!showForm && (
            <Button size="md" variant="secondary" onClick={() => setShowForm(true)}>
              Nuova richiesta
            </Button>
          )}
        </div>

        {showForm && (
          <Card tone="subtle" padding="md" className="mb-4">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <FormField label="Tipo">
                <Select
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value as LeaveType })}
                >
                  <option value="vacation">Ferie</option>
                  <option value="permit">Permesso</option>
                  <option value="sick">Malattia</option>
                </Select>
              </FormField>
              <FormField label="Da">
                <Input
                  type="date"
                  value={form.startDate}
                  onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                />
              </FormField>
              <FormField label="A">
                <Input
                  type="date"
                  value={form.endDate}
                  onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                />
              </FormField>
              <div className="sm:col-span-4">
                <FormField label="Motivo (opzionale)">
                  <Input
                    maxLength={500}
                    value={form.reason}
                    onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  />
                </FormField>
              </div>
            </div>
            <div className="flex flex-col gap-2 mt-3 sm:flex-row">
              <Button size="md" variant="primary" className="w-full sm:w-auto" onClick={submit}>
                Invia
              </Button>
              <Button
                size="md"
                variant="ghost"
                className="w-full sm:w-auto"
                onClick={() => {
                  setShowForm(false);
                  setError(null);
                }}
              >
                Annulla
              </Button>
            </div>
          </Card>
        )}

        {leaves.length === 0 ? (
          <EmptyState
            title="Nessuna richiesta"
            description="Non ci sono richieste di ferie o permessi: creane una con «Nuova richiesta»."
            size="compact"
          />
        ) : (
          <ul className="space-y-2">
            {leaves.map((l) => (
              <li
                key={l.id}
                className="border border-border-default rounded-md px-3 py-2 flex items-start sm:items-center justify-between gap-2 flex-wrap bg-elevated"
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <LeaveBadge leave={l} />
                  <Badge tone={statusTone[l.status]} variant="soft" size="xs">
                    {statusLabel[l.status]}
                  </Badge>
                  <span className="whitespace-nowrap text-mono-xs text-fg-muted">
                    {l.daysCount} gg
                  </span>
                  {l.reason && (
                    <span className="text-caption text-fg-muted italic">{l.reason}</span>
                  )}
                </div>
                {l.status === "pending" && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => void cancel(l.id)}
                    className="text-danger"
                  >
                    Annulla
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
